/**
 * Posições do grupo, do servidor — com a reciprocidade já aplicada lá.
 */
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export interface PessoaNoMapa {
  memberId: string;
  userId: string;
  name: string;
  latitude: number;
  longitude: number;
  accuracyM: number | null;
  recordedAt: string;
  me: boolean;
}

export interface Posicoes {
  sharing: boolean;
  people: PessoaNoMapa[];
}

async function ler(tripId: string): Promise<Posicoes> {
  const res = await fetch(`/api/v1/trips/${tripId}/locations`, { credentials: 'same-origin' });
  if (!res.ok) throw new Error(`Falha ao carregar (${res.status})`);
  return (await res.json()) as Posicoes;
}

/** Enquanto a tela do mapa está aberta, atualiza a cada 20 s. */
export function usePosicoes(tripId: string, ligado: boolean) {
  const consulta = useQuery({
    queryKey: ['posicoes', tripId],
    queryFn: () => ler(tripId),
    enabled: ligado,
    refetchInterval: ligado ? 20_000 : false,
    staleTime: 10_000,
  });
  const { refetch } = consulta;
  const queryClient = useQueryClient();
  // Reler assim que a nossa posição for aceita pelo servidor. Dois cuidados,
  // ambos aprendidos com o E2E `mapa` falhando de forma intermitente:
  //
  //   1. o ouvinte é registrado desde a montagem, e não só quando `ligado`
  //      vira verdadeiro — ligar e enviar a primeira posição acontecem no
  //      mesmo ciclo, e o evento chegava antes do efeito dependente de
  //      `ligado` registrar o ouvinte;
  //   2. a primeira consulta costuma partir ANTES do PUT e ainda estar em voo
  //      quando o evento chega. Nesse estado (sem dado, buscando) o
  //      `refetch()` do TanStack Query é um no-op: devolve a busca em curso em
  //      vez de refazê-la — e a resposta antiga (`sharing: false`) valia até
  //      o próximo ciclo de 20 s. Cancelar a busca em voo antes de reler
  //      garante uma resposta posterior ao PUT.
  //
  // O evento só é disparado depois de um PUT bem-sucedido da nossa posição,
  // então reler aqui é sempre pertinente.
  useEffect(() => {
    const chave = ['posicoes', tripId];
    const aoEnviar = () => {
      void queryClient.cancelQueries({ queryKey: chave }).then(() => refetch());
    };
    window.addEventListener('nv:posicao-enviada', aoEnviar);
    return () => window.removeEventListener('nv:posicao-enviada', aoEnviar);
  }, [queryClient, refetch, tripId]);
  return consulta;
}
